/*
 * sfw-motion.js — vanilla port of the Next site's ScrollMotion.tsx.
 *  - Reveal: blocks below the fold (headings, copy, cards, media, buttons) ease in as they approach the
 *    viewport — opacity + a short rise (+ a soft blur on text), staggered when several arrive together.
 *    Web Animations API with fill "backwards": nothing is hidden by inline styles, CSS transitions/hover
 *    transforms keep working (reveal uses the independent `translate` property).
 *  - Parallax: large cropped photos (object-fit: cover inside an overflow-clipped frame) drift a few percent
 *    inside their frame while scrolling, via the independent `translate` / `scale` properties.
 *  - Only scans inside .sfw-page blocks. Skips fixed/sticky UI, dialogs, [data-sfw-no-motion] and blocks that
 *    own a reveal already. Extra reveal targets can opt in with [data-sfw-reveal].
 *  - Page load (port of the Next site's entrance): on the first scan, blocks already on screen enter in
 *    reading order and the nav drops in. layout/theme.liquid adds `sfw-pre` (text, buttons and nav at
 *    opacity 0, photos left visible) in <head> before first paint so nothing flashes; it is removed as soon
 *    as the entrance starts (2.5s safety net there too). Never runs in the theme editor.
 *  - Off entirely under prefers-reduced-motion. Re-scans when a section is (re)loaded in the theme editor.
 */
(function () {
  'use strict';
  var unhide = function () { document.documentElement.classList.remove('sfw-pre'); };
  if (window.__sfwMotion) return;
  window.__sfwMotion = true;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    unhide();
    return;
  }
  var entered = !!(window.Shopify && window.Shopify.designMode); // no entrance inside the theme editor

  var EASE = 'cubic-bezier(0.22, 1, 0.36, 1)';
  var TEXT = 'h1,h2,h3,h4,p,blockquote,li';
  var CANDIDATES = [
    TEXT,
    'img',
    'figure',
    'table',
    'article',
    '[data-sfw-reveal]',
    "a[class*='btn'],a[class*='pill']",
  ].join(',');
  var EXCLUDE = [
    '.sfw-nav',
    '.sfw-drawer',
    "[role='dialog']",
    "[aria-modal='true']",
    '[data-sfw-no-motion]',
  ].join(',');

  var isPinned = function (el, cache) {
    var n = el;
    var seen = [];
    while (n && n !== document.body) {
      var c = cache.get(n);
      if (c !== undefined) {
        seen.forEach(function (s) { cache.set(s, c); });
        return c;
      }
      seen.push(n);
      var pos = getComputedStyle(n).position;
      if (pos === 'sticky' || pos === 'fixed') {
        seen.forEach(function (s) { cache.set(s, true); });
        return true;
      }
      n = n.parentElement;
    }
    seen.forEach(function (s) { cache.set(s, false); });
    return false;
  };

  var clipped = function (el) {
    for (var n = el.parentElement, i = 0; n && i < 3; n = n.parentElement, i++) {
      var cs = getComputedStyle(n);
      if (/(hidden|clip)/.test(cs.overflow + cs.overflowY)) return true;
    }
    return false;
  };

  var io = null;
  var pio = null;
  var raf = 0;
  var parallax = new Set();
  var live = new Set();
  var listening = false;

  var frame = function () {
    raf = 0;
    var vh = window.innerHeight;
    live.forEach(function (img) {
      var frameEl = img.parentElement || img;
      var r = frameEl.getBoundingClientRect();
      var p = (vh - r.top) / (vh + r.height); // 0 entering → 1 leaving
      var y = (Math.min(1, Math.max(0, p)) - 0.5) * r.height * 0.14;
      img.style.translate = '0 ' + y.toFixed(1) + 'px';
    });
  };
  var onScroll = function () {
    if (!raf) raf = requestAnimationFrame(frame);
  };

  var teardown = function () {
    if (io) io.disconnect();
    if (pio) pio.disconnect();
    io = pio = null;
    cancelAnimationFrame(raf);
    raf = 0;
    parallax.forEach(function (img) {
      img.style.scale = '';
      img.style.translate = '';
    });
    parallax.clear();
    live.clear();
  };

  var scan = function () {
    teardown();
    var roots = Array.prototype.slice.call(document.querySelectorAll('.sfw-page'));
    if (!roots.length) {
      unhide();
      return;
    }
    var vh = window.innerHeight;
    var pinCache = new Map();

    // ---------- parallax ----------
    roots.forEach(function (root) {
      root.querySelectorAll('img').forEach(function (img) {
        var cs = getComputedStyle(img);
        var r = img.getBoundingClientRect();
        if (cs.objectFit !== 'cover' || r.height < 260 || r.width < 200) return;
        if (cs.scale !== 'none' || cs.translate !== 'none') return;
        if (img.closest(EXCLUDE) || isPinned(img, pinCache) || !clipped(img)) return;
        img.style.scale = '1.15';
        parallax.add(img);
      });
    });
    pio = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) live.add(e.target);
          else live.delete(e.target);
        });
        onScroll();
      },
      { rootMargin: '10% 0px 10% 0px' }
    );
    parallax.forEach(function (img) { pio.observe(img); });

    // ---------- reveal ----------
    var found = new Set();
    var onLoad = new Set();
    roots.forEach(function (root) {
      root.querySelectorAll(CANDIDATES).forEach(function (el) {
        if (el.closest(EXCLUDE)) return;
        // long link lists (footer columns, menus) stay still; short lists reveal item by item
        var list = el.closest('ul,ol');
        if (list && list.children.length > 5) return;
        var r = el.getBoundingClientRect();
        if (!r.width || !r.height) return; // hidden variants
        if (r.top < vh) {
          // already on screen at load → page-load entrance (first scan only). Photos are already painted
          // (sfw-pre never hides them, they are the LCP), so they stay put instead of fading in again.
          if (!entered && r.bottom > 0 && !el.matches('img') && !el.querySelector('img,picture,video')) onLoad.add(el);
          return;
        }
        if (isPinned(el, pinCache)) return;
        found.add(el);
      });
    });
    // keep the outermost block only (a card animates as one, not its children)
    var outermost = function (set) {
      return Array.from(set).filter(function (el) {
        for (var n = el.parentElement; n; n = n.parentElement) if (set.has(n)) return false;
        return true;
      });
    };
    var targets = outermost(found);

    // ---------- page-load entrance ----------
    if (!entered) {
      entered = true;
      var nav = document.querySelector('.sfw-nav');
      if (nav) nav.animate([{ opacity: 0, translate: '0 -14px' }, { opacity: 1, translate: '0 0' }], { duration: 900, easing: EASE, fill: 'backwards' });
      outermost(onLoad)
        .sort(function (a, b) {
          var ra = a.getBoundingClientRect();
          var rb = b.getBoundingClientRect();
          return ra.top - rb.top || ra.left - rb.left;
        })
        .forEach(function (el, i) {
          var text = el.matches(TEXT);
          var frames = parallax.has(el)
            ? [{ opacity: 0 }, { opacity: 1 }]
            : text
              ? [{ opacity: 0, translate: '0 22px', filter: 'blur(6px)' }, { opacity: 1, translate: '0 0', filter: 'blur(0px)' }]
              : [{ opacity: 0, translate: '0 34px' }, { opacity: 1, translate: '0 0' }];
          el.animate(frames, { duration: text ? 1000 : 1200, delay: 120 + Math.min(i, 10) * 90, easing: EASE, fill: 'backwards' });
        });
    }
    unhide(); // the body fades back in while the entrance plays

    io = new IntersectionObserver(
      function (entries) {
        var arriving = entries
          .filter(function (e) { return e.isIntersecting; })
          .map(function (e) { return e.target; })
          .sort(function (a, b) {
            var ra = a.getBoundingClientRect();
            var rb = b.getBoundingClientRect();
            return ra.top - rb.top || ra.left - rb.left;
          });
        arriving.forEach(function (el, i) {
          io.unobserve(el);
          var text = el.matches(TEXT);
          var para = parallax.has(el);
          var frames = para
            ? [{ opacity: 0 }, { opacity: 1 }]
            : text
              ? [
                  { opacity: 0, translate: '0 22px', filter: 'blur(6px)' },
                  { opacity: 1, translate: '0 0', filter: 'blur(0px)' },
                ]
              : [
                  { opacity: 0, translate: '0 34px' },
                  { opacity: 1, translate: '0 0' },
                ];
          el.animate(frames, {
            duration: text ? 1000 : 1200,
            delay: Math.min(i, 6) * 80,
            easing: EASE,
            fill: 'backwards',
          });
        });
      },
      { rootMargin: '0px 0px 6% 0px', threshold: 0 }
    );
    targets.forEach(function (el) { io.observe(el); });

    if (!listening) {
      listening = true;
      window.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', onScroll);
    }
    onScroll();
  };

  var timer = 0;
  var schedule = function () {
    clearTimeout(timer);
    timer = setTimeout(scan, 40); // let the page lay out first (it is hidden by sfw-pre)
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule);
  else schedule();
  document.addEventListener('shopify:section:load', schedule);
  document.addEventListener('shopify:section:unload', schedule);
})();
